import { CommonRfiUtils, FeedItemSubType, PersonDoc, RfiDoc, RfiFeedItemDoc, RFISettingsDoc, TagData, TaskRelationship, Workflow, WorkflowState } from 'common';
import { ConcourseDb, TeamDoc } from '../../global';
import { removeDupes } from '../../global/utils/string';

/**
 * Pure utility methods for RFI document manipulation, defaults, and business logic.
 * No DB communication here — see RfiService for that.
 */
export class RfiUtils extends CommonRfiUtils {
  static getBaseRfi(states: WorkflowState[], num: string): RfiDoc {
    return {
      _id: ConcourseDb.makeShortDocId(),
      code: num,
      title: '',
      description: '',
      due_date: '',
      states,
      predecessors: [],
      successors: [],
      distribution: [],
      tags: [],
      question: '',
      suggested_answer: ''
    };
  }

  static sortWorkflowsByUpdatedDate(workflows: Workflow[] = []): Workflow[] {
    return [...workflows].sort((a, b) => (a.updated_date || '').localeCompare(b.updated_date || ''));
  }

  static getDefaultWorkflow(settings: RFISettingsDoc): Workflow {
    const sorted = this.sortWorkflowsByUpdatedDate(settings?.workflows);
    return sorted[sorted.length - 1];
  }

  static statesFromTemplate(settings: RFISettingsDoc): WorkflowState[] {
    const workflow = this.getDefaultWorkflow(settings);
    return workflow?.states;
  }

  static addDistribution(doc: RfiDoc, person: PersonDoc, userId: string) {
    if (doc.distribution.includes(person._id)) return;
    doc.distribution = [...doc.distribution, person._id];
    return doc;
  }

  static addTeamDistribution(doc: RfiDoc, team: TeamDoc, userId: string): RfiDoc {
    doc.distribution = removeDupes([...doc.distribution, ...team.members]);
    return doc;
  }

  static removeDistribution(doc: RfiDoc, personId: string, userId: string): RfiDoc {
    doc.distribution = doc.distribution.filter(id => id !== personId);
    return doc;
  }

  static addTag(doc: RfiDoc, tag: TagData): RfiDoc {
    if (doc.tags.find(t => t.name == tag.name)) return;
    doc.tags = [...doc.tags, tag];
    return doc;
  }

  static removeTag(doc: RfiDoc, tag: TagData) {
    doc.tags = doc.tags.filter(t => t.name != tag.name);
    return doc;
  }

  static isDraft(doc: RfiDoc): boolean {
    return !doc.states || !doc.created_date || doc.created_date === '' || doc.completed_state_index === undefined;
  }

  static contains(doc: RfiDoc, text: string): boolean {
    const title = this.getTitle(doc);
    return doc.code == text.toLocaleLowerCase() || title.toLocaleLowerCase().includes(text.toLocaleLowerCase());
  }

  static hasPeople(doc: RfiDoc, people: PersonDoc[]): boolean {
    return people.filter(person => RfiUtils.hasPersonId(doc, person._id)).length > 0;
  }

  static hasPersonId(doc: RfiDoc, personId: string): boolean {
    return this.getAssigneeIds(doc).includes(personId);
  }

  /** Excluded FILE/PHOTO/DRAWING/SPECIFICATION feed items for the viewer (toggle to include again). */
  static getExcludedFileLikeFeedItems(feed: RfiFeedItemDoc[]): RfiFeedItemDoc[] {
    return (feed || []).filter(
      item =>
        item.excluded &&
        !item.deleted_date &&
        !item.deleted_by &&
        (item.sub === FeedItemSubType.FILE || item.sub === FeedItemSubType.PHOTO || item.sub === FeedItemSubType.DRAWING || item.sub === FeedItemSubType.SPECIFICATION),
    );
  }

  static getWriteRoles(projectId: string, rfiId: string): string[] {
    return [`p-${projectId}-rfis-${rfiId}-write`, `p-${projectId}-rfis-write`, `p-${projectId}-write`];
  }

  static getReadRoles(projectId: string, rfiId: string): string[] {
    return [...this.getWriteRoles(projectId, rfiId), `p-${projectId}-rfis-${rfiId}`, `p-${projectId}-rfis`, `p-${projectId}`];
  }

  static renderRfiName(doc?: RfiDoc, chars?: number, fallback?: string): string {
    let fullName = '';
    if (!doc && !fallback) {
      return '';
    }

    if (!doc) {
      fullName = fallback;
    } else {
      fullName = `#${doc.code} - ${this.getTitle(doc)}`;
    }

    if (chars && fullName.length > chars) {
      return `${fullName.substring(0, chars)}...`;
    }

    return fullName;
  }
}
